// HTML Sanitizer utility
const ALLOWED_TAGS = [
  'p',
  'br',
  'strong',
  'b',
  'em',
  'i',
  'u',
  'span',
  'div',
  'ul',
  'ol',
  'li',
];
const ALLOWED_ATTRIBUTES = ['class'];

/**
 * Sanitizes HTML content by removing dangerous elements and attributes
 * @param {string} html - HTML string to sanitize
 * @returns {string} - Sanitized HTML string
 */
export const sanitizeHTML = (html) => {
  if (!html || typeof html !== 'string') return '';

  // Create a temporary DOM element
  const tempDiv = document.createElement('div');
  tempDiv.innerHTML = html;

  // Remove all script tags and their content
  const scripts = tempDiv.querySelectorAll('script');
  scripts.forEach((script) => script.remove());

  // Remove all style tags
  const styles = tempDiv.querySelectorAll('style');
  styles.forEach((style) => style.remove());

  // Remove all event attributes (onclick, onload, etc.)
  const allElements = tempDiv.querySelectorAll('*');
  allElements.forEach((element) => {
    // Remove all attributes except allowed ones
    Array.from(element.attributes).forEach((attr) => {
      if (!ALLOWED_ATTRIBUTES.includes(attr.name.toLowerCase())) {
        element.removeAttribute(attr.name);
      }
    });

    // Remove elements not in allowed tags
    if (!ALLOWED_TAGS.includes(element.tagName.toLowerCase())) {
      // Replace with span to preserve content
      const span = document.createElement('span');
      span.innerHTML = element.innerHTML;
      element.parentNode?.replaceChild(span, element);
    }
  });

  return tempDiv.innerHTML;
};

/**
 * Checks if a string contains HTML tags
 * @param {string} str - String to check
 * @returns {boolean} - True if contains HTML tags
 */
export const containsHTML = (str) => {
  if (!str || typeof str !== 'string') return false;
  return /<[^>]*>/g.test(str);
};

/**
 * Converts HTML to plain text while preserving basic formatting
 * @param {string} html - HTML string to convert
 * @returns {string} - Plain text representation
 */
export const htmlToPlainText = (html) => {
  if (!html || typeof html !== 'string') return '';

  const tempDiv = document.createElement('div');
  tempDiv.innerHTML = html;

  // Replace common HTML elements with text equivalents
  const brs = tempDiv.querySelectorAll('br');
  brs.forEach((br) => br.replaceWith('\n'));

  const ps = tempDiv.querySelectorAll('p');
  ps.forEach((p) => p.replaceWith(p.textContent + '\n\n'));

  return tempDiv.textContent || tempDiv.innerText || '';
};
